/**
 * RealtimeStatusIndicator Component
 *
 * Development-only visual indicator showing the Supabase Realtime connection status.
 * Displays a colored dot:
 * - Green: Connected/Subscribed
 * - Yellow: Connecting/Reconnecting
 * - Red: Error/Disconnected
 *
 * Only renders in development mode (NODE_ENV !== 'production')
 */

import { RealtimeStatus } from '@/hooks/useRealtimeSubscription';

interface RealtimeStatusIndicatorProps {
  /** Current connection status */
  status: RealtimeStatus;
  /** Optional label to display next to the indicator */
  label?: string;
  /** Show expanded info on hover (default: true) */
  showTooltip?: boolean;
  /** Number of reconnection attempts (shown in tooltip) */
  reconnectAttempts?: number;
}

// Status colors and labels
const statusConfig: Record<RealtimeStatus, { color: string; bgColor: string; label: string }> = {
  subscribed: {
    color: 'text-green-500',
    bgColor: 'bg-green-500',
    label: 'Connected',
  },
  connecting: {
    color: 'text-yellow-500',
    bgColor: 'bg-yellow-500',
    label: 'Connecting...',
  },
  reconnecting: {
    color: 'text-yellow-500',
    bgColor: 'bg-yellow-500',
    label: 'Reconnecting...',
  },
  disconnected: {
    color: 'text-gray-400',
    bgColor: 'bg-gray-400',
    label: 'Disconnected',
  },
  error: {
    color: 'text-red-500',
    bgColor: 'bg-red-500',
    label: 'Error',
  },
};

export function RealtimeStatusIndicator({
  status,
  label,
  showTooltip = true,
  reconnectAttempts = 0,
}: RealtimeStatusIndicatorProps) {
  // Only render in development mode
  if (import.meta.env.PROD) {
    return null;
  }

  const config = statusConfig[status];
  const isAnimating = status === 'connecting' || status === 'reconnecting';

  const tooltipText = showTooltip
    ? `Realtime: ${config.label}${reconnectAttempts > 0 ? ` (attempt ${reconnectAttempts})` : ''}`
    : undefined;

  return (
    <div
      className="inline-flex items-center gap-1.5 group relative"
      title={tooltipText}
    >
      {/* Status dot with optional pulse animation */}
      <span className="relative flex h-2.5 w-2.5">
        {isAnimating && (
          <span
            className={`absolute inline-flex h-full w-full animate-ping rounded-full ${config.bgColor} opacity-75`}
          />
        )}
        <span
          className={`relative inline-flex h-2.5 w-2.5 rounded-full ${config.bgColor}`}
        />
      </span>

      {/* Optional label */}
      {label && (
        <span className={`text-xs font-medium ${config.color}`}>
          {label}
        </span>
      )}

      {/* Tooltip on hover */}
      {showTooltip && (
        <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
          <div className="bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap shadow-lg">
            {config.label}
            {reconnectAttempts > 0 && (
              <span className="text-gray-300"> (attempt {reconnectAttempts})</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default RealtimeStatusIndicator;
